import { IconChevronRight } from '@tabler/icons-react'
import { z } from 'zod'

import {
  ONBOARDING_JOB_TITLE_OPTIONS,
  ONBOARDING_ROLE_OPTIONS,
} from 'features/onboarding/constants'
import Button from 'components/ui/button'
import RadioGroup from 'components/ui/radio-group'
import Select from 'components/ui/select'
import Text from 'components/ui/text'
import { Form } from 'components/ui/form'
import { useOnboarding } from 'features/onboarding/hooks/use-onboarding'
import {
  ONBOARDING_STEPS,
  TRANSLATION_KEYS,
} from 'features/onboarding/config/steps'
import { translate } from 'utils/translation'

interface Step2Props {
  onNext: () => void
}

const formSchema = z.object({
  roleId: z.number().nullable(),
  role: z.string(),
  jobTitle: z.string(),
})

export type FormValues = z.infer<typeof formSchema>

export const Step2 = ({ onNext }: Step2Props) => {
  const onboardingState = useOnboarding()

  const handleSubmit = (data: FormValues) => {
    onboardingState.updateFormData({ step2: data })
    onNext()
  }

  return (
    <section className="tw-mx-auto tw-flex tw-min-h-[calc(100vh-200px)] tw-w-full tw-max-w-[640px] tw-flex-col">
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-start tw-gap-4 tw-pt-6 tw-text-center md:tw-gap-8 md:tw-pt-8">
        <Text
          as="h2"
          id="step-heading"
          className="tw-m-0 tw-text-2xl tw-font-bold md:tw-text-3xl"
          boldClassName="tw-text-orange-500"
        >
          {ONBOARDING_STEPS.step2.title}
        </Text>

        <Form
          schema={formSchema}
          onSubmit={handleSubmit}
          className="tw-w-full tw-max-w-[320px]"
          defaultValues={{
            roleId: onboardingState.state.formData.step2?.roleId ?? null,
            role: onboardingState.state.formData.step2?.role ?? '',
            jobTitle: onboardingState.state.formData.step2?.jobTitle ?? '',
          }}
        >
          {({ setValue, watch, handleSubmit: onSubmit }) => {
            const roleId = watch('roleId')

            const handleRoleChange = ({ value }: { value: string }) => {
              const role = ONBOARDING_ROLE_OPTIONS.find(
                (option) => option.value === value,
              )
              setValue('role', role?.value || '')
              setValue('roleId', role?.id || null)
              setValue('jobTitle', '')
            }

            const handleJobTitleChange = (value: string) => {
              setValue('jobTitle', value)
              if (value) {
                onSubmit(handleSubmit)()
              }
            }

            return (
              <>
                <Select
                  options={ONBOARDING_ROLE_OPTIONS}
                  value={
                    onboardingState.state.formData.step2?.role || watch('role')
                  }
                  buttonClassName="!tw-py-2 !tw-h-10"
                  className="tw-w-full tw-max-w-[288px] 2xs:tw-max-w-[320px]"
                  onChange={handleRoleChange}
                  placeholder={translate(TRANSLATION_KEYS.selectOption)}
                  aria-label={translate(TRANSLATION_KEYS.selectOption)}
                />

                {(roleId || onboardingState.state.formData.step2?.roleId) && (
                  <div
                    className="tw-mt-8 tw-flex tw-w-full tw-flex-col tw-items-start tw-justify-center tw-gap-2"
                    role="group"
                    aria-labelledby="job-title-label"
                  >
                    <Text
                      as="h2"
                      id="job-title-label"
                      className="tw-text-black-100 tw-m-0 tw-text-xl tw-font-bold"
                    >
                      {TRANSLATION_KEYS.radioGroupHeading}
                    </Text>
                    <RadioGroup
                      name="job-title"
                      options={ONBOARDING_JOB_TITLE_OPTIONS.filter(
                        (option) =>
                          option.role ===
                          (roleId ??
                            onboardingState.state.formData.step2?.roleId),
                      )}
                      variant="card"
                      className="tw-w-full"
                      onChange={handleJobTitleChange}
                      aria-required="true"
                    />
                  </div>
                )}
              </>
            )
          }}
        </Form>
      </div>

      <div className="tw-mt-auto tw-flex tw-justify-center tw-gap-3 tw-py-8">
        <Button
          type="button"
          size="lg"
          variant="neutral-outlined"
          onClick={onNext}
          aria-label={translate(TRANSLATION_KEYS.skipButton)}
        >
          {translate(TRANSLATION_KEYS.skipButton)}{' '}
          <IconChevronRight aria-hidden="true" size={20} />
        </Button>
      </div>
    </section>
  )
}
